/**
 * UserListItem Component
 * 사용자 목록 아이템
 */
import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { Button } from '../atoms/Button/Button';

interface UserListItemProps {
  user: {
    uid: string;
    email: string;
    displayName: string;
    memberGrade: string;
    referredUsersCount: number;
    creditBalance: number;
    createdAt: Date | null;
  };
  onGradeChange: (uid: string) => void;
}

const GRADE_LABELS: Record<string, string> = {
  admin: '관리자',
  member: '일반회원',
  regular: '정회원',
  special: '특별회원'
};

const GRADE_COLORS: Record<string, string> = {
  admin: tokens.colors.semantic.error,
  member: tokens.colors.neutral[500],
  regular: tokens.colors.primary[500],
  special: tokens.colors.secondary[500]
};

export const UserListItem: React.FC<UserListItemProps> = ({ user, onGradeChange }) => {
  const formatDate = (date: Date | null) => {
    // 날짜가 유효한지 확인
    if (!date || isNaN(date.getTime())) {
      return '-';
    }

    try {
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      console.error('[UserListItem] Date format error:', error, date);
      return '-';
    }
  };

  return (
    <Item>
      <MainInfo>
        <Name>{user.displayName}</Name>
        <Email>{user.email}</Email>
      </MainInfo>

      <Stats>
        <StatItem>
          <StatLabel>등급</StatLabel>
          <GradeBadge $color={GRADE_COLORS[user.memberGrade]}>
            {GRADE_LABELS[user.memberGrade] || user.memberGrade}
          </GradeBadge>
        </StatItem>

        <StatItem>
          <StatLabel>추천인원</StatLabel>
          <StatValue>{user.referredUsersCount}명</StatValue>
        </StatItem>

        <StatItem>
          <StatLabel>크레딧</StatLabel>
          <StatValue>{user.creditBalance.toLocaleString()}원</StatValue>
        </StatItem>

        <StatItem>
          <StatLabel>가입일</StatLabel>
          <StatValue>{formatDate(user.createdAt)}</StatValue>
        </StatItem>
      </Stats>

      <Actions>
        <Button
          variant="secondary"
          size="small"
          onClick={() => onGradeChange(user.uid)}
        >
          등급 변경
        </Button>
      </Actions>
    </Item>
  );
};

const Item = styled.div`
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[4]};
  display: flex;
  gap: ${tokens.spacing[4]};
  align-items: center;
  transition: all ${tokens.animation.duration.fast} ${tokens.animation.easing.easeOut};

  &:hover {
    box-shadow: ${tokens.shadows.sm};
    border-color: ${tokens.colors.neutral[300]};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MainInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Name = styled.div`
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[1]};
`;

const Email = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[500]};
`;

const Stats = styled.div`
  flex: 2;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${tokens.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[500]};
`;

const StatValue = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
`;

const GradeBadge = styled.div<{ $color: string }>`
  display: inline-block;
  padding: ${tokens.spacing[1]} ${tokens.spacing[2]};
  background-color: ${props => `${props.$color}15`};
  color: ${props => props.$color};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.semibold};
  width: fit-content;
`;

const Actions = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};

  @media (max-width: 768px) {
    width: 100%;

    button {
      width: 100%;
    }
  }
`;
